import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import LoginPage from "@/pages/LoginPage";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import MobileCheckoutPage from "@/pages/MobileCheckoutPage";
import LandingPage from "@/pages/LandingPage";
import ComprarLeadsPage from "@/pages/ComprarLeadsPage";
import DashboardPage from "@/pages/DashboardPage";
import HomePage from "@/pages/HomePage";
import HistoryPage from "@/pages/HistoryPage";
import SchedulerPage from "@/pages/SchedulerPage";
import GroupsPage from "@/pages/GroupsPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AffiliatePage from "@/pages/AffiliatePage";
import AdminPayoutsPage from "@/pages/AdminPayoutsPage";
import ExtensionPage from "@/pages/ExtensionPage";
import VivoAgentsPage from "@/pages/VivoAgentsPage";
import ClaudeAgentPage from "@/pages/ClaudeAgentPage";
import WhatsAppPage from "@/pages/WhatsAppPage";
import ProspectPage from "@/pages/ProspectPage";
import MetaAdsPage from "@/pages/MetaAdsPage";
import UtmPage from "@/pages/UtmPage";
import CompanyPipelinePage from "@/pages/CompanyPipelinePage";
import CrmLandingPage from "@/pages/CrmLandingPage";
import AndroidDownloadPage from "@/pages/AndroidDownloadPage";
import Layout from "@/components/Layout";
import OnboardingModal from "@/components/OnboardingModal";
import AIAssistant from "@/components/AIAssistant";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Rota "/" pública: visitante vê a landing; usuário já logado vai direto pro painel.
const PublicHome = () => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (user) return <Navigate to="/home" replace />;
  return <LandingPage />;
};

// O assistente fica fora do <Routes>, então precisa checar a sessão por conta
// própria: sem usuário logado, não monta — o widget não existe na landing.
// Também não monta pra conta de equipe (vendedor/bko/etc.) — esse assistente
// fala sobre recursos (grupos do Facebook, campanhas) que essas contas nem
// têm acesso no sidebar, e ele vive fora do Layout.jsx (onde é a trava de
// "Acesso Negado" de verdade) — melhor nem expor o widget do que confiar só
// no fato de hoje o backend dele não consultar dado nenhum da conta.
const AIAssistantLogado = () => {
  const { user, loading } = useAuth();
  if (loading || !user || user.is_team_member) return null;
  return <AIAssistant />;
};

// Mesma lógica: onboarding não deve aparecer pra conta de equipe, já que os
// passos falam de Grupos e Campanhas (recursos que essa conta nem tem no
// sidebar) — só existe como wrapper porque precisa do useAuth(), e o
// componente App não tem "user" no próprio escopo (fica dentro do
// AuthProvider, não é filho dele diretamente aqui).
const OnboardingModalLogado = () => {
  const { user, loading } = useAuth();
  if (loading || !user || user.is_team_member) return null;
  return <OnboardingModal />;
};

// Bloqueia o site inteiro (mesmo áreas logadas) pra quem acessa pelo
// navegador de um Android — direciona pra tela de baixar o app. NÃO afeta
// o próprio app Android (Capacitor injeta window.Capacitor e reporta
// isNativePlatform() === true rodando dentro do WebView nativo — sem essa
// checagem, o app ia bloquear a si mesmo, já que o user-agent dentro dele
// também contém "Android").
function isAndroidWebBrowser() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isAndroid = /Android/i.test(ua);
  const isNativeApp = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  return isAndroid && !isNativeApp;
}

function App() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains("dark")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  if (isAndroidWebBrowser()) {
    return <AndroidDownloadPage />;
  }

  return (
    <div
      className="App"
      style={{
        minHeight: "100vh",
        ...(isDark && {
          background: "linear-gradient(135deg, #0d1530 0%, #0a1020 50%, #0e1835 100%)",
          backgroundAttachment: "fixed",
        }),
      }}
    >
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<PublicHome />} />
            <Route path="/excluir-conta" element={<DeleteAccountPage />} />
            <Route path="/app/assinar" element={<MobileCheckoutPage />} />
            <Route path="/comprar-leads" element={<ComprarLeadsPage />} />
            <Route path="/crm" element={<CrmLandingPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/painel" element={<DashboardPage />} />
              <Route path="/historico" element={<HistoryPage />} />
              <Route path="/agendamento" element={<SchedulerPage />} />
              <Route path="/grupos" element={<GroupsPage />} />
              <Route path="/planos" element={<PricingPage />} />
              <Route path="/planos/sucesso" element={<PaymentSuccessPage />} />
              <Route path="/afiliados" element={<AffiliatePage />} />
              <Route path="/admin/saques" element={<AdminPayoutsPage />} />
              <Route path="/extensao" element={<ExtensionPage />} />
              <Route path="/agentes-vivo" element={<VivoAgentsPage />} />
              <Route path="/integracao-claude" element={<ClaudeAgentPage />} />
              <Route path="/whatsapp" element={<WhatsAppPage />} />
              <Route path="/prospecto" element={<ProspectPage />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/meta-ads" element={<MetaAdsPage />} />
              <Route path="/utms" element={<UtmPage />} />
              <Route path="/empresa" element={<CompanyPipelinePage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* Onboarding aparece na primeira vez que o usuário entra — mas
              não pra conta de equipe, já que os passos falam de Grupos e
              Campanhas (recursos que essa conta nem tem no sidebar). */}
          <OnboardingModalLogado />
          {/* IA Assistente: só para usuário logado — nunca na landing pública */}
          <AIAssistantLogado />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
